/**
 * Vanilla TypeScript "discovery" client island (Phase 4). No React, no
 * hydration -- the server-rendered list/cards are always the real content;
 * this script only layers an interactive map and/or "near me" convenience
 * on top, and never mutates the URL (no history.pushState, no query
 * params) so the canonical SEO URL never changes.
 *
 * Bundled by build.mjs (browser target) to
 * dist/client/discovery.js and served statically at
 * /volunteer-opportunities/assets/discovery.js. Included only on pages
 * that pass `includeDiscoveryClient` to <Document>.
 */

interface MapDiscoveryItem {
  id: string;
  type: "organization" | "opportunity";
  title: string;
  organizationName?: string;
  description?: string;
  imageUrl?: string;
  category?: string;
  latitude?: number;
  longitude?: number;
  city: string;
  state: string;
  distanceMiles?: number;
  virtual: boolean;
  url: string;
  startDate?: string;
  recurring?: boolean;
}

interface MapExperienceData {
  mode: "map";
  mapEnabled: boolean;
  googleMapsApiKey: string | null;
  center: { lat: number; lng: number };
  zoom: number;
  items: MapDiscoveryItem[];
  radiusOptionsMiles: number[];
  maxRadiusMiles: number;
  defaultRadiusMiles: number;
  searchEndpoint: string;
  category?: string;
}

interface DiscoverySearchResponse {
  items: MapDiscoveryItem[];
  truncated: boolean;
  center: { lat: number; lng: number } | null;
}

import { groupItemsByCoordinate } from "./map-marker-position";

const ORGANIZATION_IMAGE_PLACEHOLDER =
  "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 640 360'%3E%3Crect width='640' height='360' fill='%23e9f3f6'/%3E%3Cpath d='M320 112c-44 0-80 36-80 80 0 60 80 124 80 124s80-64 80-124c0-44-36-80-80-80zm0 112a32 32 0 1 1 0-64 32 32 0 0 1 0 64z' fill='%23006b8f'/%3E%3C/svg%3E";

export {};

declare global {
  interface Window {
    google?: any;
    __nexysMapsLoading?: Promise<void>;
  }
}

function itemKey(item: MapDiscoveryItem): string {
  return `${item.type}:${item.id}`;
}

function loadGoogleMaps(apiKey: string): Promise<void> {
  if (window.google?.maps) return Promise.resolve();
  if (window.__nexysMapsLoading) return window.__nexysMapsLoading;
  window.__nexysMapsLoading = new Promise((resolve, reject) => {
    const callbackName = "__nexysGoogleMapsReady";
    const script = document.createElement("script");
    const cleanup = () => {
      delete (window as any)[callbackName];
    };
    (window as any)[callbackName] = () => {
      cleanup();
      if (window.google?.maps?.Map) resolve();
      else reject(new Error("Google Maps initialized without the Maps library"));
    };
    script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(apiKey)}&loading=async&callback=${callbackName}`;
    script.async = true;
    script.onerror = () => {
      cleanup();
      reject(new Error("Failed to load Google Maps"));
    };
    document.head.appendChild(script);
  });
  return window.__nexysMapsLoading;
}

// --- Full map experience (City/Category pages) --------------------------

function initMapExperience(): void {
  const dataScript = document.getElementById("nexys-map-data");
  const container = document.querySelector<HTMLElement>(".map-experience");
  if (!dataScript || !container) return;
  const mapExperience = container;

  let data: MapExperienceData;
  try {
    data = JSON.parse(dataScript.textContent ?? "null");
  } catch {
    return;
  }
  if (!data || !data.mapEnabled) return;

  const statusEl = container.querySelector<HTMLElement>("#map-status");
  const mapCanvas = container.querySelector<HTMLElement>("#nexys-map");
  const radiusSelect = container.querySelector<HTMLSelectElement>("#map-radius-select");
  const useLocationBtn = container.querySelector<HTMLButtonElement>("#map-use-my-location");
  const searchAreaBtn = container.querySelector<HTMLButtonElement>("#map-search-area");
  const listToggleBtn = container.querySelector<HTMLButtonElement>("#map-list-toggle");

  const setStatus = (message: string) => {
    if (statusEl) statusEl.textContent = message;
  };

  if (!data.googleMapsApiKey) {
    setStatus("Map view is unavailable right now. The full list below is up to date.");
    if (mapCanvas) mapCanvas.textContent = "Map unavailable -- see the list below for full results.";
    return;
  }

  let map: any = null;
  let markers: any[] = [];
  let activeInfoWindow: any = null;
  let currentAbortController: AbortController | null = null;

  function clearMarkers() {
    markers.forEach((marker) => marker.setMap(null));
    markers = [];
  }

  function highlightCards(keys: string[]) {
    document.querySelectorAll(".entity-card.map-highlighted").forEach((el) => {
      el.classList.remove("map-highlighted");
    });
    let firstCard: Element | null = null;
    for (const key of keys) {
      const card = document.querySelector(`[data-item-id="${key}"]`);
      if (card) {
        card.classList.add("map-highlighted");
        firstCard ??= card;
      }
    }
    firstCard?.scrollIntoView({ behavior: "smooth", block: "nearest" });
  }

  function renderMarkers(items: MapDiscoveryItem[]) {
    if (!map) return;
    clearMarkers();
    const googleMaps = window.google.maps;
    const coordinateItems = items.filter(
      (item): item is MapDiscoveryItem & { latitude: number; longitude: number } =>
        item.latitude !== undefined && item.longitude !== undefined,
    );
    for (const group of groupItemsByCoordinate(coordinateItems)) {
      const markerTitle = group.items.length === 1
        ? group.items[0].title
        : `${group.items.length} results at this location`;
      const marker = new googleMaps.Marker({
        position: {
          lat: group.coordinate.latitude,
          lng: group.coordinate.longitude,
        },
        map,
        title: markerTitle,
      });
      marker.addListener("click", () => {
        if (activeInfoWindow) activeInfoWindow.close();
        const details = group.items.map((item) =>
          `<section class="map-info-record"><h4>${escapeHtml(item.title)}</h4>${
            item.organizationName ? `<p>${escapeHtml(item.organizationName)}</p>` : ""
          }${item.category ? `<p>${escapeHtml(item.category)}</p>` : ""}${
            item.description ? `<p>${escapeHtml(item.description.slice(0, 180))}</p>` : ""
          }<p>${escapeHtml(item.city)}, ${escapeHtml(item.state)}</p><a href="${escapeAttribute(
            item.url,
          )}" target="_blank" rel="noopener noreferrer">${
            item.type === "organization" ? "Visit organization" : "View details"
          }</a></section>`
        ).join("");
        const info = new googleMaps.InfoWindow({
          content: `<div class="map-info-window">${details}</div>`,
        });
        info.open(map, marker);
        activeInfoWindow = info;
        highlightCards(group.items.map(itemKey));
      });
      markers.push(marker);
    }
  }

  function renderMapResults(items: MapDiscoveryItem[]) {
    const results = mapExperience.querySelector<HTMLElement>("#map-results");
    if (!results) return;
    results.innerHTML = "";
    for (const item of items) {
      const card = document.createElement("article");
      card.className = "entity-card";
      card.setAttribute("data-item-id", itemKey(item));
      if (item.imageUrl) {
        const image = document.createElement("img");
        image.className = "entity-image";
        image.src = item.imageUrl;
        image.alt = "";
        image.loading = "lazy";
        image.setAttribute("data-image-fallback", ORGANIZATION_IMAGE_PLACEHOLDER);
        card.appendChild(image);
      }
      if (item.category) {
        const tags = document.createElement("div");
        tags.className = "card-tag-row";
        const tag = document.createElement("span");
        tag.className = "tag";
        tag.textContent = item.category;
        tags.appendChild(tag);
        card.appendChild(tags);
      }
      const title = document.createElement("h3");
      title.textContent = item.title;
      card.appendChild(title);
      if (item.description) {
        const description = document.createElement("p");
        description.className = "entity-desc";
        description.textContent = item.description;
        card.appendChild(description);
      }
      const meta = document.createElement("div");
      meta.className = "entity-meta";
      const location = document.createElement("span");
      location.textContent = `${item.city}, ${item.state}`;
      meta.appendChild(location);
      if (item.distanceMiles !== undefined) {
        const distance = document.createElement("span");
        distance.textContent = `${item.distanceMiles.toFixed(1)} mi away`;
        meta.appendChild(distance);
      }
      card.appendChild(meta);
      const link = document.createElement("a");
      link.className = "card-link";
      link.href = item.url;
      link.target = "_blank";
      link.rel = "noopener noreferrer";
      link.textContent = item.type === "opportunity" ? "See details →" : "Visit organization →";
      card.appendChild(link);
      results.appendChild(card);
    }
    results.hidden = false;
  }

  function escapeHtml(value: string): string {
    return value.replace(/[&<>"']/g, (char) => {
      switch (char) {
        case "&":
          return "&amp;";
        case "<":
          return "&lt;";
        case ">":
          return "&gt;";
        case '"':
          return "&quot;";
        default:
          return "&#39;";
      }
    });
  }

  function escapeAttribute(value: string): string {
    return escapeHtml(value);
  }

  function attachCardHoverSync(items: MapDiscoveryItem[]) {
    const byKey = new Map(items.map((item) => [itemKey(item), item]));
    document.querySelectorAll<HTMLElement>("[data-item-id]").forEach((card) => {
      const key = card.getAttribute("data-item-id");
      if (!key || !byKey.has(key)) return;
      card.addEventListener("mouseenter", () => highlightCards([key]));
    });
  }

  async function runSearch(query: Record<string, string>, origin?: { lat: number; lng: number }) {
    if (currentAbortController) currentAbortController.abort();
    const controller = new AbortController();
    currentAbortController = controller;
    setStatus("Searching this area…");
    try {
      const params = new URLSearchParams(query);
      if (data.category) params.set("category", data.category);
      const response = await fetch(`${data.searchEndpoint}?${params.toString()}`, {
        signal: controller.signal,
      });
      if (!response.ok) {
        setStatus("We couldn't refresh results just now. The list below is still accurate.");
        return;
      }
      const result: DiscoverySearchResponse = await response.json();
      renderMarkers(result.items);
      renderMapResults(result.items);
      attachCardHoverSync(result.items);
      setStatus(
        result.items.length === 0
          ? "No results in this area. Try a larger radius."
          : `Showing ${result.items.length}${result.truncated ? "+" : ""} result${
              result.items.length === 1 ? "" : "s"
            }.`,
      );
      if (origin && map) {
        // Keep the map roughly centered without fighting user pans too much.
      }
    } catch (error) {
      if ((error as Error).name === "AbortError") return;
      setStatus("We couldn't refresh results just now. The list below is still accurate.");
    }
  }

  loadGoogleMaps(data.googleMapsApiKey)
    .then(() => {
      if (!mapCanvas) return;
      const googleMaps = window.google.maps;
      map = new googleMaps.Map(mapCanvas, {
        center: data.center,
        zoom: data.zoom,
      });
      renderMarkers(data.items);
      attachCardHoverSync(data.items);
      setStatus(`Showing ${data.items.length} result${data.items.length === 1 ? "" : "s"}.`);

      if (searchAreaBtn) {
        searchAreaBtn.hidden = false;
        map.addListener("dragend", () => {
          searchAreaBtn.hidden = false;
        });
        map.addListener("zoom_changed", () => {
          searchAreaBtn.hidden = false;
        });
        searchAreaBtn.addEventListener("click", () => {
          const bounds = map.getBounds();
          if (!bounds) return;
          const ne = bounds.getNorthEast();
          const sw = bounds.getSouthWest();
          searchAreaBtn.hidden = true;
          void runSearch({
            north: String(ne.lat()),
            south: String(sw.lat()),
            east: String(ne.lng()),
            west: String(sw.lng()),
          });
        });
      }
    })
    .catch(() => {
      setStatus("Map view is temporarily unavailable. The full list below is up to date.");
      if (mapCanvas) {
        mapCanvas.textContent = "Map unavailable -- see the list below for full results.";
      }
    });

  radiusSelect?.addEventListener("change", () => {
    const radius = Number(radiusSelect.value);
    void runSearch({
      lat: String(data.center.lat),
      lng: String(data.center.lng),
      radiusMiles: String(Math.min(radius, data.maxRadiusMiles)),
    });
  });

  useLocationBtn?.addEventListener("click", () => {
    if (!("geolocation" in navigator)) {
      setStatus("Location isn't available in this browser.");
      return;
    }
    setStatus("Finding your location…");
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        if (map) map.setCenter({ lat: latitude, lng: longitude });
        void runSearch(
          {
            lat: String(latitude),
            lng: String(longitude),
            radiusMiles: String(radiusSelect ? Number(radiusSelect.value) : data.defaultRadiusMiles),
          },
          { lat: latitude, lng: longitude },
        );
      },
      () => {
        setStatus("Location access was denied. You can still browse the list below.");
      },
      { timeout: 10_000 },
    );
  });

  listToggleBtn?.addEventListener("click", () => {
    const isListMode = mapExperience.classList.toggle("map-list-mode");
    listToggleBtn.setAttribute("aria-pressed", String(isListMode));
    listToggleBtn.textContent = isListMode ? "Show map" : "Show list";
  });
}

// --- Lightweight "near me" widget (Directory root / homepage) -----------

function initNearMeWidgets(): void {
  document.querySelectorAll<HTMLElement>("[data-near-me-widget]").forEach((widget) => {
    const button = widget.querySelector<HTMLButtonElement>("[data-near-me-button]");
    const status = widget.querySelector<HTMLElement>("[data-near-me-status]");
    const resultsContainer = widget.parentElement?.querySelector<HTMLElement>("[data-near-me-results]");
    const endpoint = widget.getAttribute("data-endpoint");
    const radius = widget.getAttribute("data-radius") ?? "25";
    if (!button || !endpoint) return;

    const setStatus = (message: string) => {
      if (status) status.textContent = message;
    };

    button.addEventListener("click", () => {
      if (!("geolocation" in navigator)) {
        setStatus("Location isn't available in this browser.");
        return;
      }
      setStatus("Finding your location…");
      button.disabled = true;
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const { latitude, longitude } = position.coords;
          try {
            const params = new URLSearchParams({
              lat: String(latitude),
              lng: String(longitude),
              radiusMiles: radius,
            });
            const response = await fetch(`${endpoint}?${params.toString()}`);
            if (!response.ok) throw new Error("Search failed");
            const result: DiscoverySearchResponse = await response.json();
            renderNearMeResults(resultsContainer, result.items);
            setStatus(
              result.items.length === 0
                ? "No nearby results yet. Try browsing by state below."
                : `Found ${result.items.length}${result.truncated ? "+" : ""} nearby result${
                    result.items.length === 1 ? "" : "s"
                  }.`,
            );
          } catch {
            setStatus("We couldn't load nearby results just now. Please browse by state below.");
          } finally {
            button.disabled = false;
          }
        },
        () => {
          setStatus("Location access was denied. You can still browse by state below.");
          button.disabled = false;
        },
        { timeout: 10_000 },
      );
    });
  });
}

function renderNearMeResults(container: HTMLElement | null | undefined, items: MapDiscoveryItem[]): void {
  if (!container) return;
  container.innerHTML = "";
  for (const item of items) {
    const card = document.createElement("article");
    card.className = "entity-card";
    card.setAttribute("data-item-id", itemKey(item));

    const title = document.createElement("h3");
    title.textContent = item.title;
    card.appendChild(title);

    const meta = document.createElement("div");
    meta.className = "entity-meta";
    const location = document.createElement("span");
    location.textContent = `${item.city}, ${item.state}`;
    meta.appendChild(location);
    if (item.distanceMiles !== undefined) {
      const distance = document.createElement("span");
      distance.textContent = `${item.distanceMiles.toFixed(1)} mi away`;
      meta.appendChild(distance);
    }
    card.appendChild(meta);

    if (item.imageUrl) {
      const image = document.createElement("img");
      image.className = "entity-image";
      image.src = item.imageUrl;
      image.alt = "";
      image.loading = "lazy";
      image.setAttribute("data-image-fallback", ORGANIZATION_IMAGE_PLACEHOLDER);
      card.insertBefore(image, title);
    }

    const link = document.createElement("a");
    link.className = "card-link";
    link.href = item.url;
    link.textContent = item.type === "opportunity" ? "See details →" : "Visit organization →";
    card.appendChild(link);

    container.appendChild(card);
  }
}

function init() {
  initMapExperience();
  initNearMeWidgets();
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", init);
} else {
  init();
}
